import type { ShortcutGroup } from "../onboardingTypes";

/**
 * The keyboard shortcut inventory.
 *
 * Every entry here was verified against source. The product has ~20 working
 * shortcuts and, until now, no cheat sheet anywhere — the only listing surface
 * was SequenceCanvasActionsPanel, visible only while a Sequence node is
 * selected, in hardcoded English.
 *
 * `Ctrl` renders as `Cmd` on Apple platforms; UseShortcutsHook matches
 * `event.ctrlKey || event.metaKey`, so every combo below works with either.
 */

const globalGroup = (searchLabelKey: string): ShortcutGroup => ({
  titleKey: "onboarding.shortcuts.group.global",
  entries: [
    { keys: ["/"], labelKey: searchLabelKey },
    { keys: ["?"], labelKey: "onboarding.shortcuts.this_list" },
    { keys: ["Ctrl", "S"], labelKey: "onboarding.shortcuts.save" },
    { keys: ["Ctrl", "Z"], labelKey: "onboarding.shortcuts.undo" },
    { keys: ["Ctrl", "Y"], labelKey: "onboarding.shortcuts.redo" },
    { keys: ["Esc"], labelKey: "onboarding.shortcuts.escape" },
  ],
});

const interactionsGroup: ShortcutGroup = {
  titleKey: "onboarding.shortcuts.group.interactions",
  entries: [
    { keys: ["Ctrl", "C"], labelKey: "onboarding.shortcuts.copy" },
    { keys: ["Ctrl", "X"], labelKey: "onboarding.shortcuts.cut" },
    { keys: ["Ctrl", "V"], labelKey: "onboarding.shortcuts.paste" },
    { keys: ["Ctrl", "D"], labelKey: "onboarding.shortcuts.duplicate" },
    { keys: ["Shift", "click"], labelKey: "onboarding.shortcuts.select_range" },
    { keys: ["Ctrl", "click"], labelKey: "onboarding.shortcuts.select_toggle" },
    { keys: ["Right-click"], labelKey: "onboarding.shortcuts.context_menu" },
  ],
  // The clipboard genuinely survives a project switch and a browser tab:
  // copyPasteClipboard.ts writes JSON to the system clipboard and falls back
  // to an in-memory envelope. Nothing in the UI has ever said so.
  footnoteKeys: ["onboarding.shortcuts.footnote.clipboard"],
};

const textFieldsGroup: ShortcutGroup = {
  titleKey: "onboarding.shortcuts.group.text",
  entries: [
    { keys: ["$"], labelKey: "onboarding.shortcuts.variable" },
    { keys: ["Ctrl", "B"], labelKey: "onboarding.shortcuts.bold" },
    { keys: ["Ctrl", "I"], labelKey: "onboarding.shortcuts.italic" },
  ],
};

/** 3D only — the sequence canvas does not exist in 360. */
const sequenceGroup: ShortcutGroup = {
  titleKey: "onboarding.shortcuts.group.sequence",
  entries: [
    { keys: ["Ctrl", "F"], labelKey: "onboarding.shortcuts.find_in_sequence" },
    { keys: ["Alt", "F"], labelKey: "onboarding.shortcuts.format_graph" },
    { keys: ["Alt", "T"], labelKey: "onboarding.shortcuts.text_note" },
    { keys: ["Alt", "C"], labelKey: "onboarding.shortcuts.comment_box" },
    { keys: ["Alt", "drag"], labelKey: "onboarding.shortcuts.splice" },
    { keys: ["Delete"], labelKey: "onboarding.shortcuts.delete_nodes" },
    { keys: ["Enter"], labelKey: "onboarding.shortcuts.connect_nodes" },
    { keys: ["Middle-drag"], labelKey: "onboarding.shortcuts.pan" },
  ],
  // Honest limitation disclosure. Delete this the day Sequence.Filter is
  // implemented — right now it returns [] with a TODO, so `/` silently cannot
  // see anything inside a sequence.
  footnoteKeys: ["onboarding.shortcuts.footnote.sequence_search"],
};

export const shortcutGroups360: ShortcutGroup[] = [
  globalGroup("onboarding.shortcuts.search_bubbles"),
  interactionsGroup,
  textFieldsGroup,
];

export const shortcutGroups3D: ShortcutGroup[] = [
  globalGroup("onboarding.shortcuts.search"),
  interactionsGroup,
  sequenceGroup,
  textFieldsGroup,
];
